/**
 * PhoneMessagesIndicator · Global header · operational communications.
 *
 * Represents the "people need you" attention channel — distinct from
 * NotificationBell which represents "the system needs you". Polls the
 * open phone messages count every 30 seconds and escalates the badge
 * colour by urgency:
 *   1-2 open  · blue    (informational)
 *   3-5 open  · amber   (attention)
 *   6+ open   · rose    (urgent)
 * When there are no open messages, renders the phone icon at neutral
 * weight without a badge.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const POLL_MS = 30_000;

function tone(n) {
  if (n <= 0) return null;
  if (n <= 2) return "bg-[color:var(--color-info)] text-white";
  if (n <= 5) return "bg-amber-500 text-white";
  return "bg-rose-600 text-white";
}

export default function PhoneMessagesIndicator() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/phone-messages", { params: { status: "open", limit: 200 } });
      const items = Array.isArray(data) ? data : (data?.items || []);
      const open = items.filter((m) => m.status !== "closed").length;
      setCount(open);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const badgeTone = tone(count);
  const label = count === 0
    ? "No open phone messages"
    : `${count} open phone message${count === 1 ? "" : "s"}`;

  return (
    <button
      data-testid="header-phone-indicator"
      onClick={() => navigate("/messages?open=1")}
      className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-secondary hover:bg-surface-alt transition-colors"
      title={label}
      aria-label={label}
    >
      <Phone size={18} weight={count > 0 ? "fill" : "regular"} />
      {count > 0 ? (
        <span
          data-testid="header-phone-badge"
          className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold leading-none inline-flex items-center justify-center ${badgeTone}`}
        >
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
    </button>
  );
}
